/**
 * INTEGRATION BASE SELECTOR COMPONENT
 * 
 * Componente reutilizável para seleção de integrações da base (disponíveis para vincular)
 * Extraído de AgentIntegrations.tsx para ser usado em múltiplos contextos
 * 
 * Diferença do IntegrationsSelector:
 * - IntegrationsSelector: seleciona integrações já vinculadas
 * - IntegrationBaseSelector: seleciona integrações da base disponíveis
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IntegrationBase, Integration } from "../types/integration";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Card, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Plus, Search, Sparkles } from "lucide-react";
import { IntegrationBaseService } from "../services/integrationsBaseService";
import { IntegrationService } from "../services/integrationsService";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useWorkspace } from "@/features/Workspaces/state/WorkspaceContext";
import { getIntegrationDescription } from "@/shared/utils/i18nHelper";
import { IntegrationsForm } from "./IntegrationsForm";
import { Separator } from "@/shared/components/ui/separator";


// Interfaces
interface IntegrationFilter {
  type: 'classification' | 'name' | 'type';
  value: string;
}

interface IntegrationBaseSelectorProps {
  /** Se o flyout está aberto */
  open: boolean;
  /** Callback para fechar o flyout */
  onClose: () => void;
  /** Callback ao selecionar uma integração */
  onSelect: (integration: IntegrationBase) => void;
  /** Lista de IDs de integrações já selecionadas (para filtrar) */
  selectedIntegrations?: string[];
  /** Filtros para aplicar na busca */
  filters?: IntegrationFilter;
  /** Título customizado do flyout */
  title?: string;
  /** Placeholder customizado da busca */
  searchPlaceholder?: string;
  /** Permitir seleção múltipla */
  multipleSelection?: boolean;
}

export function IntegrationBaseSelector({ 
  open, 
  onClose, 
  onSelect, 
  selectedIntegrations = [],
  filters,
  title,
  searchPlaceholder,
  multipleSelection = false
}: IntegrationBaseSelectorProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { selectedOrganization } = useSuperAdminOrg();
  const { currentWorkspace } = useWorkspace();
  const effectiveOrgId = selectedOrganization?.id ?? user?.organizationId;
  const [integrations, setIntegrations] = useState<IntegrationBase[]>([]);
  const [templates, setTemplates] = useState<Integration[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Integration | null>(null);

  useEffect(() => {
    if (open) {
      fetchIntegrations();
      setSearchQuery(""); // Limpa a busca ao abrir o menu
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIntegrations, filters]);

  const fetchIntegrations = async () => {
    setIsLoading(true);
    try {
      const userOrganizationId = effectiveOrgId;

      if (!userOrganizationId) {
        console.error('User organization ID not available');
        setIntegrations([]);
        return;
      }

      // Parâmetros de filtro para buscar integrações base
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filterParams: any = {
        status: 'active',
        ...(currentWorkspace?.id ? { product_id: currentWorkspace.id } : {})
      };

      // Busca todas as integrações base ativas da organização
      const allIntegrations = await IntegrationBaseService.getAll(filterParams);
      
      // Filtra apenas as integrações que não estão já selecionadas
      let availableIntegrations = allIntegrations.filter(
        (integration: IntegrationBase) => 
          !selectedIntegrations.includes(integration.id)
      );

      // 🔍 Aplicar filtros se especificados
      if (filters) {
        availableIntegrations = availableIntegrations.filter((integration: IntegrationBase) => {
          switch (filters.type) {
            case 'classification':
              return integration.classification === filters.value;
            case 'name':
              return integration.name === filters.value;
            case 'type':
              return integration.classification === filters.value;
            default:
              return true;
          }
        });
      }
      
      setIntegrations(availableIntegrations || []);

      // 🎨 Buscar templates (integrations) com os mesmos filtros
      // Usar getAll() sem parâmetros para obter todos os templates disponíveis
      const allTemplates = await IntegrationService.getAll();
      
      // Filtrar templates bloqueadas (block: true) - não devem aparecer para seleção
      const selectableTemplates = allTemplates.filter((template: Integration) => template.settings?.block !== true);
      
      // Aplicar os mesmos filtros nos templates
      let filteredTemplates = selectableTemplates;
      if (filters) {
        filteredTemplates = selectableTemplates.filter((template: Integration) => {
          switch (filters.type) {
            case 'classification':
              return template.classification === filters.value;
            case 'name':
              return template.name === filters.value;
            case 'type':
              return template.classification === filters.value;
            default:
              return true;
          }
        });
      }
      
      // Ordenar: ativas primeiro, depois inativas
      const sortedTemplates = (filteredTemplates || []).sort((a, b) => {
        const aActive = a.active !== false;
        const bActive = b.active !== false;
        
        if (aActive === bActive) return 0;
        return aActive ? -1 : 1;
      });
      
      setTemplates(sortedTemplates);
    } catch (error) {
      setIntegrations([]);
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectIntegration = (integration: IntegrationBase) => {
    onSelect(integration);
    
    // Fechar apenas se não for seleção múltipla
    if (!multipleSelection) {
      onClose();
    }
  };

  // 🎨 Abrir formulário para criar nova integração a partir de template
  const handleSelectTemplate = (template: Integration) => {
    setSelectedTemplate(template);
    setIsFormOpen(true);
  };

  // ✅ Callback após criar integração com sucesso
  const handleIntegrationCreated = async () => {
    setIsFormOpen(false);
    setSelectedTemplate(null);
    
    // Recarregar integrações para incluir a recém-criada
    await fetchIntegrations();
    
    // Buscar a integração recém-criada e auto-selecionar
    // Como não temos o ID imediatamente, vamos buscar todas novamente
    // e selecionar a primeira que não estava na lista anterior
    if (effectiveOrgId) {
      const updatedIntegrations = await IntegrationBaseService.getAll({
        status: 'active'
      });
      
      // Encontrar a nova integração (a que não estava na lista anterior)
      const newIntegration = updatedIntegrations.find(
        (integration: IntegrationBase) =>
          !integrations.some((prev) => prev.id === integration.id)
      );
      
      if (newIntegration) {
        onSelect(newIntegration);
        if (!multipleSelection) {
          onClose();
        }
      }
    }
  };

  // 🔍 Filtrar integrações com base na busca
  const filteredIntegrations = integrations.filter((integration: IntegrationBase) => {
    const description = getIntegrationDescription(integration, i18n.language);
    return (integration.label || integration.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (description?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (integration.classification?.toLowerCase() || '').includes(searchQuery.toLowerCase());
  });

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>
            {title || t("agents.settings.integrations.selectTitle")}
          </SheetTitle>
        </SheetHeader>

        {/* 🔍 Campo de Busca */}
        <div className="relative mt-4">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder || t("agents.settings.integrations.searchPlaceholder")}
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* 📊 Resumo de Resultados */}
        <div className="mt-2 text-xs text-muted-foreground">
          {isLoading ? (
            <span>{t('common.loading')}</span>
          ) : (
            <span>
              {t('integrations.selector.resultCount', { count: filteredIntegrations.length })}
              {filters ? ` (${t('integrations.selector.filtered', { count: filteredIntegrations.length })})` : ''}
              {searchQuery ? ` ${t('integrations.selector.forQuery', { query: searchQuery })}` : ''}
            </span>
          )}
        </div>

        {/* 📋 Lista de Integrações */}
        <ScrollArea className="mt-4 h-[calc(100vh-12rem)]">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-8">
              {t('common.messages.loading')}
            </div>
          ) : (
            <div className="space-y-4">
              {/* 📋 Integrações já instaladas */}
              {filteredIntegrations.length > 0 ? (
                <div className="space-y-2">
                  {filteredIntegrations.map((integration) => (
                    <Card
                      key={integration.id}
                      className="cursor-pointer transition-colors hover:bg-muted"
                      onClick={() => handleSelectIntegration(integration)}
                    >
                      <CardHeader className="flex flex-row items-center gap-4 space-y-0 p-4">
                        {/* 🖼️ Logo/Avatar da Integração */}
                        {integration?.logo_url ? (
                          <img 
                            src={integration.logo_url} 
                            alt={integration.label || integration.name || 'Integration'} 
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                            <span className="text-xl font-medium">
                              {(integration.label || integration.name || 'I')[0]?.toUpperCase()}
                            </span>
                          </div>
                        )}

                        {/* 📝 Informações da Integração */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base">
                              {integration.title || integration.label || integration.name}
                            </CardTitle>
                          </div>
                          <CardDescription className="text-sm">
                            {getIntegrationDescription(integration, i18n.language) || t('agents.settings.integrations.noDescription')}
                          </CardDescription>
                        </div>

                        {/* ➕ Ícone de Adicionar */}
                        <div className="ml-4">
                          <Plus className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <h3 className="text-base font-medium text-foreground">
                    {searchQuery 
                      ? t('agents.settings.integrations.noSearchResults')
                      : filters
                        ? t('integrations.selector.requiredNotInstalled')
                        : t('agents.settings.integrations.noIntegrationsAvailable')
                    }
                  </h3>
                </div>
              )}

              {/* 🎨 Seção de Templates (Adicionar Nova Integração) */}
              {templates.length > 0 && !searchQuery && (
                <>
                  {filteredIntegrations.length > 0 && <Separator className="my-4" />}
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground px-1">
                      <Sparkles className="h-4 w-4" />
                      <span>
                        {filteredIntegrations.length > 0
                          ? t('integrations.selector.addNewPrompt')
                          : t('integrations.selector.addFirstPrompt')}
                      </span>
                    </div>
                    
                    {templates.map((template) => {
                      const isInactive = template.active === false;
                      
                      return (
                        <Card
                          key={template.id}
                          className={`transition-colors border-dashed ${
                            isInactive
                              ? 'opacity-40 grayscale cursor-not-allowed'
                              : 'cursor-pointer hover:bg-primary/5'
                          }`}
                          onClick={() => !isInactive && handleSelectTemplate(template)}
                        >
                          <CardHeader className="flex flex-row items-center gap-3 space-y-0 p-3">
                            {/* 🖼️ Logo/Avatar do Template */}
                            {template?.logo_url ? (
                              <img 
                                src={template.logo_url} 
                                alt={template.label || template.name || 'Template'} 
                                className="h-8 w-8 rounded-md object-cover opacity-70"
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary">
                                  {(template.label || template.name || 'T')[0]?.toUpperCase()}
                                </span>
                              </div>
                            )}

                            {/* 📝 Informações do Template */}
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-sm truncate">
                                {template.label || template.name}
                              </CardTitle>
                              <CardDescription className="text-xs line-clamp-1">
                                {getIntegrationDescription(template, i18n.language) || t('integrations.selector.templateDescription')}
                              </CardDescription>
                            </div>

                            {/* ➕ Ícone de Criar */}
                            <div>
                              <Plus className={`h-4 w-4 ${isInactive ? 'text-muted-foreground' : 'text-primary'}`} />
                            </div>
                          </CardHeader>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </ScrollArea>
      </SheetContent>

      {/* 🎨 Formulário de Criação de Integração */}
      {selectedTemplate && (
        <IntegrationsForm
          isOpen={isFormOpen}
          onOpenChange={setIsFormOpen}
          selectedTemplate={selectedTemplate}
          mode="create"
          onSuccess={handleIntegrationCreated}
        />
      )}
    </Sheet>
  );
}
